import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setUserFromVerification, clearUser } from "../../store/slices/authSlice";
import authService from "../../components/services/AuthService";
import Images from "../../utils/Images";

const AccountVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [otp, setOtp] = useState(["", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const inputRefs = useRef([]);
  const userEmail = location.state?.email || "your email";
  const userPhone = location.state?.phone || null;
  const userData = location.state?.userData || null;

  const affiliateCode =
    userData?.affiliate_code ||
    userData?.affiliateCode ||
    user?.affiliate_code ||
    user?.affiliateCode ||
    (() => {
      try {
        const tempUser = JSON.parse(
          localStorage.getItem("dbx_aff_temp_user") || "{}"
        );
        return tempUser.affiliate_code || tempUser.affiliateCode;
      } catch {
        return null;
      }
    })();
    
  const verificationMethod = userEmail ? "email" : "phone";
  const verificationTarget = userEmail || userPhone || "your contact";

  // Logout function to clear state and redirect to landing
  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    dispatch(clearUser());
    navigate("/landing");
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  useEffect(() => {
    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Handle escape key to close logout confirmation
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showLogoutConfirm) {
        setShowLogoutConfirm(false);
      }
    };

    if (showLogoutConfirm) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showLogoutConfirm]);

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 4);
    const newOtp = [...otp];

    for (let i = 0; i < pastedData.length && i < 4; i++) {
      newOtp[i] = pastedData[i];
    }

    setOtp(newOtp);
    setError("");

    // Focus the last filled input or the next empty one
    const lastFilledIndex = Math.min(pastedData.length - 1, 3);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleVerify = async () => {
    const otpCode = otp.join("");

    if (otpCode.length !== 4) {
      setError("Please enter the complete 4-digit code");
      return;
    }

    if (!affiliateCode) {
      setError("Affiliate code not found. Please try registering again.");
      return;
    }

    setIsVerifying(true);
    setError("");
    setSuccess("");

    try {
      // Use the correct payload format with affiliate_code and otp
      const payload = {
        affiliate_code: affiliateCode,
        otp: parseInt(otpCode),
      };

      const response = await authService.verifyEmail(payload);

      if (response.status) {
        setSuccess(
          `${
            verificationMethod === "email" ? "Email" : "Phone"
          } verified successfully! Redirecting...`
        );

        // Clean up temporary verification data
        localStorage.removeItem("dbx_aff_temp_user");
        localStorage.removeItem("dbx_aff_verification_email");

        // Store user data and token in Redux
        dispatch(setUserFromVerification(response.data));

        // Redirect to home page after a short delay
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setError(response.message || "Verification failed. Please try again.");
      }
    } catch (error) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError("");
    setSuccess("");

    try {
      const payload =
        verificationMethod === "email"
          ? { emailOrPhone: userEmail }
          : { emailOrPhone: userPhone };

      const response = await authService.resendVerificationCode(payload);

      if (response.status) {
        setSuccess("Verification code sent successfully!");
        setCountdown(30);
        setCanResend(false);

        // Restart countdown
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              setCanResend(true);
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(
          response.message || "Failed to resend code. Please try again."
        );
      }
    } catch (error) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
      <div className="overflow-hidden fixed inset-0">
        <img
          src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
          alt="Background"
          className="object-cover w-full h-full opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-red-50/80 via-orange-50/85 to-yellow-50/80"></div>
        <div className="absolute inset-0 bg-gradient-to-r animate-pulse from-red-500/5 via-orange-500/5 to-yellow-500/5"></div>
      </div>

      {/* Main Container */}
      <div className="relative z-10 w-full min-h-screen">
        <div className="grid grid-cols-1 min-h-screen lg:grid-cols-3 lg:items-center">
          {/* Left Side - Verification Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col min-h-screen bg-white lg:h-screen lg:col-span-2"
          >
            <div className="overflow-y-auto flex-1 pb-4 scrollbar-thin scrollbar-thumb-red-400 scrollbar-track-transparent hover:scrollbar-thumb-red-500">
              {/* Header */}
              <div className="flex flex-col p-4 mb-4 space-y-4 text-left sm:flex-row sm:justify-between sm:items-center sm:space-y-0 sm:p-6 lg:p-8">
                <img src={Images.logo} alt="Dealboxx Logo" className="h-8 w-auto max-w-[120px] sm:h-auto sm:max-w-none" />
                <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm font-medium text-center text-gray-600 bg-gray-100 rounded-lg transition-colors hover:text-red-600 hover:bg-gray-200 sm:px-0 sm:py-0 sm:bg-transparent sm:text-left"
                  >
                    Back to Login
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 text-sm font-medium text-center text-white bg-red-600 rounded-lg transition-colors hover:text-white hover:bg-red-700 sm:px-0 sm:py-0 sm:bg-transparent sm:text-red-600 sm:hover:bg-transparent sm:hover:text-red-700"
                  >
                    Cancel & Logout
                  </button>
                </div>
              </div>

              {/* Verification Content */}
              <div className="px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mx-auto max-w-md"
                >
                  {/* Verification Icon */}
                  <div className="flex justify-center mb-6">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        delay: 0.4,
                        type: "spring",
                        stiffness: 200,
                      }}
                      className="flex justify-center items-center w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-full shadow-lg shadow-red-500/25"
                    >
                      <svg
                        className="w-10 h-10 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </motion.div>
                  </div>

                  {/* Title and Description */}
                  <div className="mb-8 text-center">
                    <motion.h1
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="mb-2 text-2xl font-bold text-gray-900"
                    >
                      Verify Your{" "}
                      {verificationMethod === "email" ? "Email" : "Phone"}
                    </motion.h1>
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="text-gray-600"
                    >
                      We've sent a 4-digit verification code to your{" "}
                      {verificationMethod}
                    </motion.p>
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="font-semibold text-red-600"
                    >
                      {verificationTarget}
                    </motion.p>
                  </div>

                  {/* OTP Input */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="mb-6"
                  >
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      Enter Verification Code
                    </label>
                    <div className="flex justify-center space-x-2 sm:space-x-3">
                      {otp.map((digit, index) => (
                        <motion.input
                          key={index}
                          ref={(el) => (inputRefs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength="1"
                          value={digit}
                          onChange={(e) =>
                            handleOtpChange(index, e.target.value)
                          }
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          onPaste={handlePaste}
                          className={`w-12 h-12 sm:w-14 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                            digit
                              ? "text-red-700 bg-red-50 border-red-500"
                              : "text-gray-700 bg-white border-gray-300 hover:border-red-300"
                          }`}
                          disabled={isVerifying}
                        />
                      ))}
                    </div>
                  </motion.div>

                  {/* Error/Success Messages */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-3 mb-4 bg-red-50 rounded-lg border border-red-200"
                      >
                        <p className="text-sm text-center text-red-600">
                          {error}
                        </p>
                      </motion.div>
                    )}
                    {success && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-3 mb-4 bg-green-50 rounded-lg border border-green-200"
                      >
                        <p className="text-sm text-center text-green-600">
                          {success}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Verify Button */}
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 }}
                    onClick={handleVerify}
                    disabled={isVerifying || otp.join("").length !== 4}
                    className="px-4 py-3 w-full font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-all duration-200 transform hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isVerifying ? (
                      <div className="flex justify-center items-center">
                        <svg
                          className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Verifying...
                      </div>
                    ) : (
                      `Verify ${
                        verificationMethod === "email" ? "Email" : "Phone"
                      }`
                    )}
                  </motion.button>

                  {/* Resend Code */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 }}
                    className="mt-6 text-center"
                  >
                    <p className="mb-3 text-sm text-gray-600">
                      Didn't receive the code?
                    </p>
                    <button
                      onClick={handleResend}
                      disabled={!canResend || isResending}
                      className={`text-sm font-medium transition-colors duration-200 ${
                        canResend && !isResending
                          ? "text-red-600 hover:text-red-700 cursor-pointer"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      {isResending ? (
                        <div className="flex justify-center items-center">
                          <svg
                            className="mr-2 -ml-1 w-4 h-4 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Sending...
                        </div>
                      ) : canResend ? (
                        "Resend Code"
                      ) : (
                        `Resend in ${countdown}s`
                      )}
                    </button>
                  </motion.div>

                  {/* Back to Registration */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1 }}
                    className="mt-8 text-center"
                  >
                    <Link
                      to="/register"
                      className="text-sm text-gray-600 transition-colors duration-200 hover:text-red-600"
                    >
                      ← Back to Registration
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:justify-center lg:items-center lg:flex lg:flex-col lg:h-90 lg:p-4"
          >
            <div className="flex overflow-hidden flex-col p-6 h-full bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-2xl border shadow-2xl backdrop-blur-lg border-red-200/50">
              <div className="flex-shrink-0 mb-3 text-center">
                <h2 className="mb-1 text-lg font-bold text-red-800">
                  Almost There!
                </h2>
                <p className="text-xs text-red-700">
                  Complete verification to start earning
                </p>
              </div>

              <div className="overflow-y-auto flex-1 space-y-4 min-h-0 scrollbar-thin scrollbar-thumb-red-400 scrollbar-track-transparent hover:scrollbar-thumb-red-500">
                {[
                  {
                    icon: "🔐",
                    title: "Secure Verification",
                    description:
                      "Your account is protected with email verification",
                  },
                  {
                    icon: "⚡",
                    title: "Quick Setup",
                    description:
                      "Get started in minutes with our simple process",
                  },
                  {
                    icon: "📈",
                    title: "Start Earning",
                    description:
                      "Access your dashboard and begin promoting deals",
                  },
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                    className="flex items-start p-3 space-x-3 rounded-lg border shadow-sm backdrop-blur-sm bg-white/80 border-red-200/50"
                  >
                    <div className="text-2xl">{feature.icon}</div>
                    <div>
                      <h3 className="mb-1 text-sm font-semibold text-red-800">
                        {feature.title}
                      </h3>
                      <p className="text-xs text-red-600">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="flex-shrink-0 mt-2 text-center"
              >
                <div className="p-3 bg-gradient-to-r from-red-500 to-red-600 rounded-lg border border-red-400 shadow-lg backdrop-blur-sm">
                  <h3 className="mb-1 text-sm font-semibold text-white">
                    Verification Required
                  </h3>
                  <p className="text-xs text-red-100">
                    Check your {verificationMethod} for the 4-digit code
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="p-4 mx-4 w-full max-w-md bg-white rounded-lg shadow-xl sm:p-6"
            >
              <div className="text-center">
                <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-red-100 rounded-full">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">
                  Cancel Verification?
                </h3>
                <p className="mb-6 text-sm text-gray-500">
                  Are you sure you want to cancel the verification process? 
                  You'll need to register again to access your account.
                </p>
                <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={cancelLogout}
                    className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-md border border-gray-300 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Continue Verification
                  </button>
                  <button
                    onClick={confirmLogout}
                    className="flex-1 px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-md border border-transparent transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Yes, Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccountVerification;
